import { DATE_TIME_FORMATS } from "@miplatform/constants";
import {
  IQuickBooksAuditListResponse,
  QuickBookSyncStatus,
  QuickBooksType,
} from "@miplatform/types";
import { formatDate, getCurrencyString } from "@miplatform/utils";

export type optionsType = { label: string; value: string };

export type Filters = {
  sortBy: string;
  status: QuickBookSyncStatus[];
  accountId: string;
  communityId: string;
  quickBooksType: QuickBooksType | "All";
};

export const sortByOptions = [
  { label: "Sync Date", value: "lastSyncDate" },
  { label: "Status", value: "status" },
];

export const syncTypeOptions = [
  { label: "Invoices", value: QuickBooksType.Invoice },
  { label: "Payments", value: QuickBooksType.Payment },
  { label: "Homes", value: QuickBooksType.Home },
  { label: "Customers", value: QuickBooksType.Account },
  { label: "CreditMemo", value: QuickBooksType.CreditMemo },
  { label: "Account Retainage", value: QuickBooksType.Account_Retainage },
  { label: "Home Retainage", value: QuickBooksType.Home_Retainage },
  { label: "Vendors", value: QuickBooksType.Vendor },
  { label: "All", value: "All" },
];

export const syncOptionValues = [
  { label: "Synced", value: QuickBookSyncStatus.Synced },
  { label: "Pending Processing", value: QuickBookSyncStatus.PendingProcessing },
  { label: "Not Synced", value: QuickBookSyncStatus.NotSynced },
  { label: "Failed", value: QuickBookSyncStatus.Failed },
  { label: "Partial Sync", value: QuickBookSyncStatus.PartialSync },
];

export const QUICKBOOKS_TYPES_ALL: QuickBooksType[] = [
  QuickBooksType.Invoice,
  QuickBooksType.Payment,
  QuickBooksType.Home,
  QuickBooksType.Account,
  QuickBooksType.CreditMemo,
  QuickBooksType.Account_Retainage,
  QuickBooksType.Home_Retainage,
  QuickBooksType.Vendor,
];

export const LIST_ITEM_WIDTH = 100;

export interface ListConfigField {
  label: string;
  fieldKey: string;
  valueGetter?: (row: IQuickBooksAuditListResponse) => any;
  width?: number;
}

export const LIST_CONFIG: Record<
  QuickBooksType,
  { fields: ListConfigField[] }
> = {
  [QuickBooksType.Invoice]: {
    fields: [
      {
        label: "Address",
        fieldKey: "addressLine1",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          row.additionalData ? JSON.parse(row.additionalData).addressLine1 : "",
        width: 200,
      },
      {
        label: "Invoice Amount",
        fieldKey: "invoiceAmount",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          getCurrencyString(
            row.additionalData
              ? JSON.parse(row.additionalData).invoiceAmount || 0
              : 0
          ),
      },
      {
        label: "Invoiced Order",
        fieldKey: "invoicedOrder",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          row.additionalData
            ? JSON.parse(row.additionalData).orderTemplateName
            : "",
      },
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Home]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.CreditMemo]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Payment]: {
    fields: [
      { label: "Customer", fieldKey: "accountName" },
      {
        label: "Payment Type",
        fieldKey: "paymentType",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          row.additionalData ? JSON.parse(row.additionalData).paymentType : "",
      },
      {
        label: "Payment Amount",
        fieldKey: "paymentAmount",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          getCurrencyString(
            row.additionalData
              ? JSON.parse(row.additionalData).paymentAmount || 0
              : 0
          ),
      },
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Account]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Vendor]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Home_Retainage]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
  [QuickBooksType.Account_Retainage]: {
    fields: [
      { label: "Sync Type", fieldKey: "quickBooksType" },
      { label: "Sync Status", fieldKey: "status" },
      {
        label: "Sync Date",
        fieldKey: "lastSyncDate",
        valueGetter: (row: IQuickBooksAuditListResponse) =>
          formatDate(
            row.lastSyncDate,
            DATE_TIME_FORMATS.FORMAT_MM_DD_YY_HH_MM_A
          ),
        width: 160,
      },
    ],
  },
};
